/**
 * Format a date for display in message lists
 * - Time (e.g., "2:30 PM") if today
 * - "Yesterday" if yesterday
 * - Day of week (e.g., "Tuesday") if within last 7 days
 * - Date (e.g., "Dec 5") if this year
 * - Full date (e.g., "Dec 5, 2024") if older
 */
export function formatMessageDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();

  // Reset times to midnight for day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const diffMs = today.getTime() - dateDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Today - show time
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < 7) {
    // Within last week - show day name
    return d.toLocaleDateString([], { weekday: 'long' });
  }

  if (d.getFullYear() === now.getFullYear()) {
    // This year - show month and day
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  // Older - show full date
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format a date for chat message timestamps
 * Shows time only (e.g., "2:30 PM")
 */
export function formatMessageTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/**
 * Format a date in a friendly, compact way:
 * - Today
 * - Yesterday
 * - Tue (short day name if within 7 days)
 * - Jan 3 (if this year)
 * - Feb 2017 (if prior year - month and year only)
 */
export function formatFriendlyDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();

  // Reset times to midnight for day comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const diffMs = today.getTime() - dateDay.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < 7) {
    // Within last week - show short day name
    return d.toLocaleDateString([], { weekday: 'short' });
  }

  if (d.getFullYear() === now.getFullYear()) {
    // This year - show month and day
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  // Prior year - show month and year only (no day)
  return d.toLocaleDateString([], { month: 'short', year: 'numeric' });
}
